<template>
  <video
    tabindex="0"
    ref="videoPlayer"
    class="video-js vjs-default-skin vjs-big-play-centered"
    :class="{ 'vjs-fluid': fluid }"
    @keydown.up="setVolume"
    @keydown.down="setVolume"
    @keydown.j="player.currentTime(player.currentTime() - 10)"
    @keydown.l="player.currentTime(player.currentTime() + 10)"
    @keydown.c="toggleCaptions"
  ></video>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import { throttle } from "lodash";
import config from "#/config";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "jb-videojs-hls-quality-selector";

import * as fr from "video.js/dist/lang/fr.json";
import store from "#/stores";
import { GET_SIGNED_URL } from "@/libvue/apps/catalog/screeners/actions";

// Define props
const props = defineProps({
  options: {
    type: Object,
    default: () => ({})
  },
  fluid: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["onPlayerReady"]);

// Define refs
const videoPlayer = ref(null);
let player = null;
let nbError = 0;

const defaultVideoOptions = {
  controls: true,
  preload: "none",
  // audioPosterMode: true,
  controlBar: {
    skipButtons: {
      forward: 10,
      backward: 10
    },
    currentTimeDisplay: true
  },
  responsive: true,
  playbackRates: [0.5, 0, 75, 1, 1.5, 2],
  fluid: props.fluid,
  html5: {
    vhs: {
      overrideNative: true
    },
    nativeAudioTracks: false,
    nativeVideoTracks: false
  }
};

const fetchNewUrl = () => {
  return store
    .dispatch(GET_SIGNED_URL, props.options.screener.uid)
    .then((data) => {
      return data;
    })
    .catch((error) => {
      throw error;
    });
};

const updateVideoSource = async (time = 0) => {
  const newUrl = await fetchNewUrl();
  player.currentTime(time);
  player.src({ type: "application/x-mpegURL", src: newUrl.data.signed_url });
  player.play();
};

// Initialize video.js player on mounted
onMounted(() => {
  const videoOptions = {
    ...defaultVideoOptions,
    ...props.options
  };
  videojs.addLanguage("fr", {
    ...fr,
    ...fr.default,
    Quality: "Qualité",
    Francais: "Français"
  });
  const sessionId = ref("");

  player = videojs(videoPlayer.value, videoOptions, () => {
    emit("onPlayerReady", player);
    player.hlsQualitySelector({
      displayCurrentQuality: true
    });

    const updatePlayhead = throttle(function (e) {
      if (e) {
        if (typeof window.analytics_media_updatePlayhead === "function") {
          window.analytics_media_updatePlayhead(
            sessionId.value,
            Math.floor(player.currentTime())
          );
        }
      }
    }, 1000);
    // Events
    // error
    player.on("error", () => {
      const mediaError = player.error();
      const currentTime = player.currentTime();

      if ((mediaError.code === 4 || mediaError.code === 2) && nbError < 1) {
        nbError++;
        updateVideoSource(currentTime);
      }
    });
    // mediaMetadataLoaded (loadedmetadata)
    player.on("loadedmetadata", async () => {
      const screener = props.options.screener;
      const playhead = Math.floor(player.currentTime());
      const playerName = config.decodedHost;

      const payload = {
        event: "mediaMetadataLoaded",
        content: {
          id: screener.uid,
          title: screener.title,
          length: screener.duration,
          type: screener.type
        }
      };
      window.dataLayerQueue.push(payload);

      // get sessionId
      if (typeof window.analytics_media_init === "function") {
        sessionId.value = await window.analytics_media_init(
          screener.uid,
          playhead,
          playerName
        );
      }
    });
    // timeupdate
    player.on("timeupdate", (e) => {
      updatePlayhead(e);
    });
    // contentPlay (playing)
    player.on("playing", async () => {
      if (!sessionId.value) return;      
      // send all events for metrie purposes
      const events = ["mediaStart", "contentStart", "contentPlay"];
      const playhead = Math.floor(player.currentTime());

      const dataLayerEvents = events.map((event) => ({
        event,
        sessionId: sessionId.value,
        playhead,
      }))

      window.dataLayerQueue.push(...dataLayerEvents)
    });
    // seekStart
    player.on("seeking", () => {
      if (!sessionId.value) return;
      dataLayerQueue.push({
        event: "seekStart",
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });
    // seekComplete
    player.on("seeked", async () => {
      if (!sessionId.value) return;
      dataLayerQueue.push({
        event: "seekComplete",
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });
    // contentPause
    player.on("pause", async () => {
      if (!sessionId.value) return;
      dataLayerQueue.push({
        event: "contentPause",
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      });
    });
    // contentComplete (ended)
    player.on("ended", async (e) => {
      if (!sessionId.value) return;
      const payload = {
        event: "contentComplete",
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      };
      window.dataLayerQueue.push(payload);
    });
    // mediaEnd (dispose)
    player.on("dispose", async (e) => {
      if (!sessionId.value) return;
      const payload = {
        event: "mediaEnd",
        sessionId: sessionId.value,
        playhead: Math.floor(player.currentTime())
      };
      window.dataLayerQueue.push(payload);
    });
  });
});

// Dispose of the player before unmounting the component
onBeforeUnmount(() => {
  if (player) {
    player.dispose();
  }
});
watch(
  () => props.options.sources,
  (newValues) => {
    if (player && newValues.length > 0) {
      player.src(newValues);
      nbError = 0; // Reset nb error when sources change
    }
  },
  { deep: true }
);
const setVolume = (e) => {
  e.preventDefault();

  if (e.code === "ArrowUp") {
    player.volume(player.volume() + 0.1);
  } else {
    player.volume(player.volume() - 0.1);
  }
};
const toggleCaptions = () => {
  const tracks = player.textTracks();
  const track = tracks.tracks_.find((track) => track.language !== "");

  if (track) {
    if (track.mode == "showing") {
      track.mode = "disabled";
    } else {
      track.mode = "showing";
    }
  }
};
</script>

<style lang="scss">
//SKIN custom (with progress bar at top)

//Play Button
.vjs-big-play-button:focus-visible {
  outline: 2.5px solid #02929a;
  outline-offset: 2px;
}

//Control bar
.vjs-control-bar {
  font-size: 12px;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 4%,
    rgba(0, 0, 0, 0.8) 100%
  ) !important;
}

// Progress control
.vjs-progress-control {
  position: absolute !important;
  bottom: 26px; /* The height of the ControlBar minus 4px. */
  left: 0;
  right: 0;
  width: 100% !important;
  height: 10px; /* the height must be reduced from 30 to 10px in order to allow the buttons below (e.g. play) to be pushed */
}
.vjs-progress-holder {
  /* needed to have a real 100% width display. */
  margin-left: 0px;
  margin-right: 0px;
  .vjs-play-progress,
  .vjs-load-progress,
  .vjs-load-progress div {
    border-radius: 6px !important;
  }
}

// Time control
.vjs-time-control {
  &.vjs-current-time,
  &.vjs-duration,
  &.vjs-time-divider {
    display: block;
    // OR display: flex;
  }

  &.vjs-current-time {
    padding-right: 6px;
  }

  &.vjs-duration {
    padding-left: 6px;
    margin-right: auto;
  }

  &.vjs-time-divider {
    padding-left: 0;
    padding-right: 0;
    min-width: auto;
  }

  &.vjs-remaining-time {
    display: none;
    margin-right: auto;
  }
}

// Skip control
.vjs-skip-forward-10,
.vjs-skip-backward-10 {
  width: 38px !important;
  .vjs-icon-placeholder::before {
    font-size: 1rem;
    top: 4px;
  }
}

//Control with menu
.vjs-menu .vjs-menu-content {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background-color: rgba(43, 51, 63, 1) !important;
}

.vjs-menu li {
  border-bottom: 1px solid #757575;
  margin: 0 0.25rem;
  font-size: 14px;

  &:first-child {
    margin-top: 0.25rem;
  }
  &:last-child {
    margin-bottom: 0.25rem;
    border-bottom: none;
  }
}
// Subtitles control
.vjs-subs-caps-button {
  .vjs-menu .vjs-menu-content {
    width: calc(100% + 2rem);
    right: 0;
  }
  .vjs-menu li {
    padding: 0.5rem 0.25rem;
    line-height: 1.1;
    text-transform: none;
    span::first-letter {
      text-transform: uppercase;
    }
  }
}
// Playback rate control
.vjs-playback-rate .vjs-playback-rate-value {
  font-size: 14px;
  line-height: 2.5;
}

//Modal Subtiltes Settings
.vjs-text-track-settings .vjs-modal-dialog-content {
  padding: 20px 24px 10px 24px;
  font-size: 14px;
}

.vjs-track-settings-controls button {
  background-image: none;
  background-color: #d1121b;
  font-weight: 600;
  color: white;
  padding: 0.4rem 1rem;
  border-radius: 4px;
  &:hover {
    color: white;
    background-color: #a70e16;
  }
}

.vjs-control.vjs-close-button {
  font-size: 1rem;
}

.vjs-text-track-settings fieldset span {
  display: inline-block;
  padding: 0 1.5rem 0.8em 0;
}

.vjs-text-track-settings fieldset span > select,
.vjs-track-setting > select {
  background-color: #dfdfdf;
  border-radius: 6px;
  border-color: #dfdfdf;
  border-width: 4px;
}

.vjs-track-setting > select {
  margin-bottom: 1.2em;
}

//BREAKPOINTS

// tiny to small
.vjs-layout-tiny,
.vjs-layout-x-small,
.vjs-layout-small {
  .vjs-picture-in-picture-control {
    display: none;
  }
  .vjs-menu-button-popup .vjs-menu .vjs-menu-content {
    max-height: 100px;
  }
  .vjs-playback-rate {
    display: none !important;
  }
  .vjs-time-control {
    &.vjs-current-time,
    &.vjs-duration,
    &.vjs-time-divider {
      display: none !important;
      // OR display: flex;
    }
    &.vjs-remaining-time {
      display: block !important;
    }
  }
  .vjs-progress-control {
    display: block !important;
    bottom: 4px;
  }
}

// tiny to medium
.vjs-layout-tiny,
.vjs-layout-x-small,
.vjs-layout-small,
.vjs-layout-medium {
  .vjs-control {
    width: 2.25rem;
  }
  .vjs-control-bar {
    font-size: 10px;
  }
  .vjs-skip-forward-10,
  .vjs-skip-backward-10 {
    width: 32px !important;
    .vjs-icon-placeholder::before {
      font-size: 14px;
      top: 3px;
    }
  }

  .vjs-playback-rate {
    display: block !important;
    .vjs-playback-rate-value {
      font-size: 10px;
      line-height: 3;
    }
  }

  .vjs-menu li {
    border-bottom: 1px solid #757575;
    margin: 0 0.15rem;
    font-size: 12px;

    &:first-child {
      margin-top: 0.25rem;
    }
    &:last-child {
      margin-bottom: 0.25rem;
      border-bottom: none;
    }
  }

  .vjs-text-track-settings .vjs-modal-dialog-content {
    padding: 10px;
    font-size: 12px;
  }
}

//small to medium
.vjs-layout-small,
.vjs-layout-medium {
  .vjs-menu-button-popup .vjs-menu .vjs-menu-content {
    max-height: 200px;
  }
}
</style>
